import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Trash2, LogOut, Mail, RefreshCw } from "lucide-react";
import { fetchMessages, deleteMessage, clearToken, formatApiError } from "../lib/adminApi";

export default function AdminDashboard({ user, onLogout }) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const m = await fetchMessages();
      setMessages(m);
    } catch (e) {
      toast.error(formatApiError(e));
      if (e?.response?.status === 401) onLogout();
    } finally {
      setLoading(false);
    }
  }, [onLogout]);

  useEffect(() => {
    load();
  }, [load]);

  const logout = () => {
    clearToken();
    onLogout();
  };

  const removeMessage = async (id) => {
    if (!window.confirm("Eliminare definitivamente questo messaggio?")) return;
    try {
      await deleteMessage(id);
      setMessages((m) => m.filter((x) => x.id !== id));
      toast.success("Messaggio eliminato.");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="min-h-screen bg-[#f0ead9] px-6 py-8 md:px-14">
      <div className="mx-auto max-w-5xl">
        <div className="flex flex-col gap-4 border-b border-[#16181a]/15 pb-6 md:flex-row md:items-center md:justify-between">
          <div>
            <p className="overline mb-2">Area Riservata</p>
            <h1 className="font-display text-3xl text-[#16181a] md:text-4xl">
              Ciao, {user?.name || "Admin"}
            </h1>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={load}
              data-testid="admin-refresh"
              className="flex items-center gap-2 border border-[#16181a]/20 px-4 py-2 font-body text-sm text-[#2a2b2d] hover:text-[#c0442b]"
            >
              <RefreshCw size={15} /> Aggiorna
            </button>
            <button
              onClick={logout}
              data-testid="admin-logout"
              className="flex items-center gap-2 border border-[#16181a]/20 px-4 py-2 font-body text-sm text-[#2a2b2d] hover:text-[#c0442b]"
            >
              <LogOut size={15} /> Esci
            </button>
          </div>
        </div>

        <div className="mt-8">
          <div className="mb-6 flex items-baseline gap-4">
            <span className="font-display text-xl italic text-[#c0442b]">01</span>
            <span className="overline">Messaggi ricevuti</span>
            {messages.length > 0 && (
              <span className="font-body text-sm text-[#6e6a64]">({messages.length})</span>
            )}
          </div>

          {loading ? (
            <p className="mt-16 font-body text-[#6e6a64]">Caricamento...</p>
          ) : (
            <div className="flex flex-col gap-4" data-testid="admin-messages-list">
              {messages.length === 0 && (
                <div className="border border-[#16181a]/15 bg-[#eae3d0] p-10 text-center">
                  <Mail size={26} className="mx-auto text-[#6e6a64]" />
                  <p className="mt-4 font-body text-[#6e6a64]">
                    Nessun messaggio ricevuto per ora.
                  </p>
                </div>
              )}
              {messages.map((m) => (
                <div
                  key={m.id}
                  data-testid={`message-${m.id}`}
                  className="border border-[#16181a]/15 bg-[#eae3d0] p-6"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <p className="font-display text-xl text-[#16181a]">{m.name}</p>
                      <a
                        href={`mailto:${m.email}`}
                        className="link-underline font-body text-sm text-[#c0442b]"
                      >
                        {m.email}
                      </a>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="font-body text-xs text-[#6e6a64]">
                        {new Date(m.created_at).toLocaleString("it-IT")}
                      </span>
                      <button
                        data-testid={`delete-message-${m.id}`}
                        onClick={() => removeMessage(m.id)}
                        className="text-[#6e6a64] hover:text-[#c0442b]"
                        aria-label="Elimina"
                      >
                        <Trash2 size={17} />
                      </button>
                    </div>
                  </div>
                  <p className="mt-4 font-body text-sm leading-relaxed text-[#2a2b2d]">
                    {m.message}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
